"use client";

import NoResult from "@/components/errors/no-result";
import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {useAppSelector} from "@/redux/hooks";
import SwrPagination from "@/components/common/pagination/swr-pagination";
import {useProducts} from "@/hooks/useProducts";
import {Box, Grid, Skeleton} from "@mui/material";
import ProductsHeader from "./products-header";
import {RootState} from "@/redux/store";
import ProductCardUnified from "@/components/product-card/product-card-unified";
import ProductCardSkeleton from "@/components/product-card/product-card-skeleton";

export default function ProductsContainer({
                                               initialData,
                                               initialParams,
                                               initialQueryString,
                                           }: {
    initialParams?: Record<string, any>;
    initialData: { data: any[] };
    initialQueryString: string;
}) {
    const {products, productsData, page, isExists, isValidating, isLoading,} = useProducts(initialData, initialQueryString);

    const productsParams = useAppSelector((state: RootState) => state.products);

    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [filtersChanged, setFiltersChanged] = useState(false);
    const resultsRef = useRef<HTMLDivElement>(null);
    const prevParamsRef = useRef(productsParams);
    const prevLengthRef = useRef<number | null>(null);
    const previousCompletedResultCountRef = useRef<number | null>(null);
    const preserveScrollRef = useRef(false);

    const cardView = isExists ?? initialParams?.isExists;

    useEffect(() => {
        if (!isValidating && !initialLoadComplete) {
            setInitialLoadComplete(true);
        }
    }, [isValidating, initialLoadComplete]);

    const showSkeletons = initialLoadComplete && filtersChanged && (isLoading || isValidating);

  /*  useEffect(() => {
        if (hasMounted.current) {
            window.scrollTo({top: 0, behavior: "smooth"});
        }
        hasMounted.current = true;
    }, [page]);*/

    useEffect(() => {
        const previousParams = prevParamsRef.current;
        const prev = JSON.stringify(previousParams);
        const next = JSON.stringify(productsParams);

        if (prev !== next) {
            setFiltersChanged(true);
            preserveScrollRef.current = Object.keys(productsParams).some(
                key =>
                    key !== "Page" &&
                    JSON.stringify(previousParams[key]) !== JSON.stringify(productsParams[key])
            );
        }

        prevParamsRef.current = productsParams;
    }, [productsParams]);

    useEffect(() => {
        if (!isLoading && !isValidating) {
            setFiltersChanged(false);
        }
    }, [isLoading, isValidating]);

    /*

        useEffect(() => {
            if (productsParams && Object.keys(productsParams).length > 0) {
                setFiltersReady(true);
            }
        }, [productsParams]);
    */

    useEffect(() => {
        if (preserveScrollRef.current) {
            prevLengthRef.current = products.length;
            return;
        }

        if (isLoading) {
            window.scrollTo({top: 0, behavior: "smooth"});
            return;
        }

        if (prevLengthRef.current !== null && products.length !== prevLengthRef.current) {
            window.scrollTo({top: 0, behavior: "smooth"});
        }

        prevLengthRef.current = products.length;
    }, [products, isLoading]);

    useEffect(() => {
        if (isLoading || isValidating) return;

        const currentResultCount = productsData?.totalCount ?? products.length;
        const previousResultCount = previousCompletedResultCountRef.current;

        previousCompletedResultCountRef.current = currentResultCount;

        if (previousResultCount !== null && previousResultCount > 0 && currentResultCount === 0) {
            resultsRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }
    }, [isLoading, isValidating, products.length, productsData?.totalCount]);



    return (
        <Box
            id="products-list"
            ref={resultsRef}
            sx={{
                display: "flex",
                flex: 1,
                flexDirection: "column",
                scrollMarginTop: {
                    xs: "65px",
                    sm: "115px",
                },
            }}
        >
            <ProductsHeader
                isLoading = {isLoading}
                productsData = {productsData ?? null}
            />

            {showSkeletons ? (
                <Grid container spacing={{ xs: 2, md:cardView ? 3 :2 }} >
                    {Array.from({length: 6}).map((_, index) => (
                        <Grid
                            item
                            key = {index}
                            xs = {12}
                            sm = {cardView ? 4 : 12}
                            sx = {{display: "block"}}
                        >
                            {cardView ? (
                                <ProductCardSkeleton />
                            ) : (
                                <Skeleton variant = "rectangular" width = "100%" height = {140} />
                            )}
                        </Grid >
                    ))}
                </Grid >
            ) : products.length <= 0 && !isLoading ? (
                <NoResult imageSrc = "/images/icons/no-product.svg" />
            ) : (
                <>
                    <Grid container spacing={{ xs: 2, md:cardView ? 3 :2 }}>
                        {products.map((item, index) => (
                            <Grid
                                key={item.id}
                                item
                                xs={12}
                                sm={cardView ? 4 : 12}
                                sx={{ display: "block" }}
                            >
                                <ProductCardUnified
                                    productData={item}
                                    offer={false}
                                    variant={cardView ? "grid" : "list"}
                                    priorityImage={index === 0}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    <SwrPagination
                        currentPage = {page || 0}
                        numberOfPages = {(productsData as any)?.totalPages ?? 0}
                    />
                </>
            )}
        </Box >
    );
}
